// connect/guideShots.ts — the connect guide's pictures, fetched AND decoded
// before they are needed.
//
// The guide shows real screens for each subject (connect · permissions ·
// instructions · import). Each subject mounts its own strip, so moving between
// them used to start a fresh fetch and decode every time: the box sat empty for
// a beat and then the picture snapped in. Now the moment the window opens (or a
// row of the list is pointed at), every picture the tool has, across every
// subject, is fetched and decoded once, and held here for the life of the page,
// so a strip that mounts later paints at once.
//
// One cache per src, shared by every caller. A failed fetch is forgotten, so the
// next open tries again.

import type { ConnectTool, GuidePhase } from './tools'

/** the subjects with pictures, in the order the guide walks them */
const PHASES: GuidePhase[] = ['mcp', 'permissions', 'instructions', 'import']

/** Every picture a tool's guide shows, in the order it shows them, once each. */
export function guideShotSrcs(tool: Pick<ConnectTool, 'guide'> | null | undefined): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const phase of PHASES) {
    for (const shot of tool?.guide?.[phase] ?? []) {
      if (!shot?.src || seen.has(shot.src)) continue
      seen.add(shot.src)
      out.push(shot.src)
    }
  }
  return out
}

const pending = new Map<string, Promise<void>>()
// the Image objects themselves are held, so the decoded pictures stay resident
// and an <img> with the same src paints from memory rather than decoding again
const held = new Map<string, HTMLImageElement>()
const decoded = new Set<string>()

/** Has this picture already been fetched and decoded? */
export function isShotReady(src: string): boolean {
  return decoded.has(src)
}

/** Fetch and decode one picture, once. Resolves either way (a picture that
 *  cannot load shows its words); never rejects. */
export function preloadShot(src: string): Promise<void> {
  const hit = pending.get(src)
  if (hit) return hit
  if (typeof Image === 'undefined') return Promise.resolve()
  const img = new Image()
  img.decoding = 'async'
  img.src = src
  held.set(src, img)
  const done =
    typeof img.decode === 'function'
      ? img.decode()
      : new Promise<void>((resolve, reject) => {
          img.onload = () => resolve()
          img.onerror = () => reject(new Error('image'))
        })
  const p = done.then(
    () => {
      decoded.add(src)
    },
    () => {
      // forgotten, so the next open tries again
      pending.delete(src)
      held.delete(src)
    },
  )
  pending.set(src, p)
  return p
}

/** Warm every picture a tool's guide shows, across every subject. */
export function preloadGuideShots(tool: Pick<ConnectTool, 'guide'> | null | undefined): Promise<void> {
  return Promise.all(guideShotSrcs(tool).map(preloadShot)).then(() => undefined)
}

/** for tests: forget everything */
export function __resetGuideShots(): void {
  pending.clear()
  held.clear()
  decoded.clear()
}
